import { useMemo } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { resolveTenantIdForPreferencesQuery } from '../utils/companyTenantResolver';

/**
 * Canonical company tenant for user preferences RTK cache keys and PUT /users/preferences.
 * Matches Sidebar / Unibox / EA: selectedCompanyTenantId first, then activeTenantId, then host default.
 */
export function useTenantIdForPreferences(): string | null {
  const { selectedCompanyTenantId, activeTenantId } = useAccount();
  return useMemo(
    () => resolveTenantIdForPreferencesQuery(selectedCompanyTenantId, activeTenantId),
    [selectedCompanyTenantId, activeTenantId]
  );
}

/** Argument for useGetUserPreferencesQuery — never pass undefined so cache keys stay tenant-scoped. */
export function useUserPreferencesQueryArg(): { tenantId: string } | undefined {
  const id = useTenantIdForPreferences();
  return id ? { tenantId: id } : undefined;
}

/**
 * Spread onto `updateUserPreferences` / `uploadCompanyLogo` args so HTTP tenant resolution
 * matches {@link useUserPreferencesQueryArg}: include `tenantId` when the hook resolves one;
 * omit it when null so the API client uses the same `getActiveTenantIdFallback()` as unscoped reads.
 */
export function preferencesTenantWriteSpread(tenantId: string | null): { tenantId: string } | Record<string, never> {
  return tenantId ? { tenantId } : {};
}
